import { z } from 'zod';

import { useAxleQuery } from '../apiHelpers';

const appConfigSchema = z.object({
    demo: z.object({
        patient_oasis_status: z.boolean(),
    }),
    episode_of_care: z.object({
        view: z.boolean(),
        write: z.boolean(),
    }),
    visit: z.object({
        page: z.boolean(),
        export: z.boolean(),
        create: z.boolean(),
        draw_routes_on_visits_page: z.boolean(),
        link_to_travel_path_debug_page: z.boolean(),
        show_demand_partner: z.boolean(),
        link_to_visit_order: z.boolean(),
        qualifications: z.boolean(),
        edit: z.object({
            status: z.boolean(),
            outcome: z.boolean(),
            datetime: z.boolean(),
            address: z.boolean(),
            assign: z.boolean(),
            cancel: z.boolean(),
            service: z.boolean(),
            dialog: z.boolean(),
            patient: z.boolean(),
            priority: z.boolean(),
            position_offset: z.boolean(),
            position_duration_override: z.boolean(),
            visit_duration_override: z.boolean(),
            contact: z.boolean(),
            any: z.boolean(),
            bulk_edit: z.boolean(),
        }),
    }),
    visit_request: z.object({
        page: z.boolean(),
        edit: z.boolean(),
    }),
    route_optimizer: z.object({
        page: z.boolean(),
        edit: z.boolean(),
        preview_clinician_view: z.boolean(),
    }),
    scheduling: z.object({
        page: z.boolean(),
        require_filters: z.boolean(),
        manage_shift_block_requests: z.boolean(),
    }),
    orders: z.object({
        feature: z.boolean(),
        view: z.boolean(),
        write: z.boolean(),
    }),
    patient: z.object({
        page: z.boolean(),
        edit: z.boolean(),
        call: z.boolean(),
        create_visit_button: z.boolean(),
        notes: z.boolean(),
        link_to_patient_portal: z.boolean(),
        show_both_booking_modes_temporary: z.boolean(),
        show_create_patient_button: z.boolean(),
    }),
    qhp: z.object({
        page: z.boolean(),
        basic_view: z.boolean(),
        create: z.boolean(),
        trigger_setup: z.boolean(),
        deactivate: z.boolean(),
        call: z.boolean(),
        teams_view: z.boolean(),
        teams_edit: z.boolean(),
        clinician_roles: z.boolean(),
    }),
    downstream_handshake: z.object({
        page: z.boolean(),
        create: z.boolean(),
    }),
    territories: z.object({
        page: z.boolean(),
        write: z.boolean(),
        lock: z.boolean(),
        service_areas: z.boolean(),
        qhps: z.boolean(),
    }),
    service_area: z.object({
        page: z.boolean(),
        create: z.boolean(),
        deactivate: z.boolean(),
        modify: z.boolean(),
        show_in_clinic: z.boolean(),
    }),
    shift: z.object({
        view: z.boolean(),
        page: z.boolean(),
        create: z.boolean(),
        edit: z.boolean(),
        qualification_restrictions: z.boolean(),
    }),
    shift_block: z.object({
        view: z.boolean(),
        write: z.boolean(),
    }),
    operating_hour_block: z.object({
        page: z.boolean(),
        write: z.boolean(),
    }),
    service: z.object({
        page: z.boolean(),
        create: z.boolean(),
    }),
    upstream_handshake: z.object({
        page: z.boolean(),
        create: z.boolean(),
    }),
    inventory: z.object({
        page: z.boolean(),
        create: z.boolean(),
    }),
    settings: z.object({
        page: z.boolean(),
        demand_config: z.boolean(),
        user: z.object({
            view: z.boolean(),
            write: z.boolean(),
            roles: z.boolean(),
        }),
    }),
    qualification: z.object({
        create: z.boolean(),
    }),
    service_orders: z.object({
        page: z.boolean(),
        create: z.boolean(),
    }),
    insights: z.object({
        page: z.boolean(),
    }),
    reports: z.object({
        page: z.boolean(),
    }),
    should_display_maps_and_metrics_in_booking: z.boolean(),
    should_display_metrics_by_date_in_booking: z.boolean(),
    enable_multiposition: z.boolean(),
    is_demo_org: z.boolean(),
    llm_notes_parsing_debug: z.boolean(),
    care_team_case_manager_label_for_rn: z.boolean(),
});
export type AppConfig = z.infer<typeof appConfigSchema>;

export const useAppConfig = (): AppConfig =>
    useAxleQuery({
        path: '/app-config',
        suspense: true,
        responseSchema: z.object({ app_config: appConfigSchema }),
    }).data.app_config;
